import React, { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import {
  Button,
  Grid2,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import { css } from '@emotion/css';
import { useDispatch, useSelector } from 'react-redux';
import { useLogoutMutation } from '@services/authApi';
import { UserState } from '@features/Auth/user.slice';
import { useTranslation } from 'react-i18next';
import { RootState } from '@redux/store';

export interface IUserMenu {
  username: string;
  accountName: string;
  menuItems: IUserMenuItems[];
}

export interface IUserMenuItems {
  id: string;
  message: ReactNode;
  link: string;
  roleRequired?: string;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
}

const styles = {
  container: css`
    height: 100%;
  `,
  menu: {
    button: css`
      margin: 0;
      padding: 0 !important;
      min-width: 130px;
    `,
    accountInfo: {
      container: css`
        flex-direction: column !important;
        justify-content: center;
        align-items: center;
        height: 100%;
      `,
      text: css`
        display: block;
        text-align: center;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        font-size: inherit !important;
        line-height: inherit !important;
        margin: -0.2rem !important;
      `,
    },
  },
};

const UserMenu: React.FC<IUserMenu> = ({ username, menuItems }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const [deleteLogout] = useLogoutMutation();
  const [anchorElement, setAnchorElement] = React.useState<null | HTMLElement>(
    null
  );
  const open = Boolean(anchorElement);

  const arrowStyle = {
    transition: '0.2s ease-in-out',
    rotate: open ? '180deg' : '0',
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorElement(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorElement(null);
  };

  const userState: UserState = useSelector((state: RootState) => state.user);

  const truncateUsernameUserMenu = () => {
    return username?.length > 17
      ? `${username?.substring(0, 17 - 3)}...`
      : username;
  };

  const tryLogout = () => {
    deleteLogout({})
      .then(() => {
        dispatch({ type: 'LOGOUT' });
      })
      .catch((error) => {
        console.error(`${t('error_in_delete_operation')}\n`, error);
      });
  };

  const handleItemClick = (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
    item: IUserMenuItems
  ) => {
    if (item.onClick) {
      item.onClick(e);
    }
    handleClose();
  };

  return (
    <Grid2 container className={styles.container}>
      <Button
        data-cy="header-menu-button"
        onClick={handleClick}
        fullWidth
        sx={{
          color: theme.palette.secondary.main,
          ml: { xs: '.9rem', sm: '0' },
        }}
        className={styles.menu.button}
      >
        <Grid2
          className={styles.menu.accountInfo.container}
          container
          sx={{
            display: { xs: 'none', lg: 'flex' },
            alignItems: 'flex-start',
          }}
        >
          <Tooltip title={username} arrow>
            <Typography className={styles.menu.accountInfo.text}>
              {truncateUsernameUserMenu()}
            </Typography>
          </Tooltip>
          <Typography className={styles.menu.accountInfo.text}>
            {userState.user.station}
          </Typography>
        </Grid2>
        <Grid2
          container
          sx={{
            alignItems: 'center',
            color: theme.palette.secondary.main,
            borderRadius: '4px',
            ml: '1rem',
            py: '.4rem',
          }}
        >
          <KeyboardArrowDown fontSize="small" sx={arrowStyle} />
        </Grid2>
      </Button>
      <Menu
        anchorEl={anchorElement}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={open}
        onClose={handleClose}
      >
        {menuItems.map((item: IUserMenuItems) =>
          !item.roleRequired ||
          userState?.user?.roles?.includes(item?.roleRequired) ? (
            <MenuItem
              component={Link}
              to={item.link}
              onClick={(e) => handleItemClick(e, item)}
              key={item.id}
            >
              {item.message}
            </MenuItem>
          ) : null
        )}
        <MenuItem data-cy="sign-out-button" onClick={tryLogout} key="logout">
          {t('sign_out')}
        </MenuItem>
      </Menu>
    </Grid2>
  );
};

export default UserMenu;
